import {
  Axis,
  AxisSetExtremesEventCallbackFunction,
  AxisSetExtremesEventObject,
  GeoJSON,
  NavigatorXAxisOptions,
  OptionsChartZoomingTypeValue,
} from 'highcharts';
import { Subject } from 'rxjs';
import { LayoutBounds } from '../widgets/layout';

type HighchartsAxis<T extends 'xAxis' | 'yAxis' | 'zAxis'> = T extends 'xAxis'
  ? Highcharts.XAxisOptions
  : T extends 'yAxis'
  ? Highcharts.YAxisOptions
  : Highcharts.ZAxisOptions;

export type ChartOptionsZoomCallback =
  | AxisSetExtremesEventCallbackFunction
  | Subject<[Axis, AxisSetExtremesEventObject]>;

export class ChartOptionsBuilder {
  public constructor(private options: Highcharts.Options) {}

  public get(): Highcharts.Options {
    return this.options;
  }

  public setType(type: string | undefined): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      type: type,
    };
    return this;
  }

  public setTitle(title: string | undefined): ChartOptionsBuilder {
    this.options.title = {
      text: title,
    };

    return this;
  }

  public removeAxisTitles(): ChartOptionsBuilder {
    this.manipulateAxis('xAxis', axisOptions => ({
      ...axisOptions,
      title: undefined,
    }));

    this.manipulateAxis('yAxis', axisOptions => ({
      ...axisOptions,
      title: undefined,
    }));

    if (this.options.zAxis) {
      this.manipulateAxis('xAxis', axisOptions => ({
        ...axisOptions,
        title: undefined,
      }));
    }
    return this;
  }

  public setMapData(data: string | object | any[] | GeoJSON | undefined): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      animation: false,
      map: data,
      backgroundColor: '#f6f6f6',
      spacing: [30, 0, 30, 0],
    };

    this.options.lang = {};

    this.options.mapNavigation = {
      ...this.options.mapNavigation,
      enableButtons: false,
    };
    return this;
  }

  public setSeries(series: Highcharts.SeriesOptionsType[]): ChartOptionsBuilder {
    this.options.series = series;
    return this;
  }

  public setXAxis(xAxisOptions: Highcharts.XAxisOptions): ChartOptionsBuilder {
    this.options.xAxis = xAxisOptions;
    return this;
  }

  public updateXAxis(xAxisOptions: Partial<Highcharts.XAxisOptions>): ChartOptionsBuilder {
    this.options.xAxis = {
      ...this.options.xAxis,
      ...xAxisOptions,
    };
    return this;
  }

  public setYAxis(yAxisOptions: Highcharts.YAxisOptions): ChartOptionsBuilder {
    this.options.yAxis = yAxisOptions;
    return this;
  }

  public updateYAxis(yAxisOptions: Partial<Highcharts.YAxisOptions>): ChartOptionsBuilder {
    this.options.yAxis = {
      ...this.options.yAxis,
      ...yAxisOptions,
    };
    return this;
  }

  public setTooltip(tooltipOptions: Highcharts.TooltipOptions): ChartOptionsBuilder {
    this.options.tooltip = tooltipOptions;
    return this;
  }

  public updateTooltip(tooltipOptions: Partial<Highcharts.TooltipOptions>): ChartOptionsBuilder {
    this.options.tooltip = {
      ...this.options.tooltip,
      ...tooltipOptions,
    };
    return this;
  }

  public setTooltipEnabled(enabled: boolean | undefined): ChartOptionsBuilder {
    this.options.tooltip = {
      ...this.options.tooltip,
      enabled: enabled === true,
    };
    return this;
  }

  public setLegendEnabled(enabled: boolean | undefined): ChartOptionsBuilder {
    this.options.legend = {
      ...this.options.legend,
      enabled: enabled === true,
    };
    return this;
  }

  public setLayout(layout: LayoutBounds | undefined): ChartOptionsBuilder {
    if (!layout) {
      return this;
    }

    this.options.chart = {
      ...this.options.chart,
      width: layout.width,
      height: layout.height,
    };
    return this;
  }

  public hideXAxis(): ChartOptionsBuilder {
    this.options.xAxis = {
      labels: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      lineWidth: 0,
      minorGridLineWidth: 0,
      minorTickLength: 0,
      tickLength: 0,
      minPadding: 0,
      maxPadding: 0,
    };
    return this;
  }

  public hideYAxis(): ChartOptionsBuilder {
    this.options.yAxis = {
      visible: false,
      title: {
        text: undefined,
      },
      labels: {
        enabled: false,
      },
      minPadding: 0,
      maxPadding: 0,
    };
    return this;
  }

  public setBorderSpacingMargin(values: {
    borderWidth?: number;
    plotShadow?: boolean;
    plotBorderWidth?: number;
    margin?: number | [number, number, number, number];
    marginTop?: number;
    marginRight?: number;
    marginBottom?: number;
    marginLeft?: number;
    spacing?: [number, number, number, number];
  }): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      ...values,
    };
    return this;
  }

  public removeBorderSpacingMargin(): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      borderWidth: 0,
      plotShadow: false,
      plotBorderWidth: 0,
      margin: 0,
      spacing: [0, 0, 0, 0],
    };
    return this;
  }

  public removePlotlinesAndMarkers(): ChartOptionsBuilder {
    return this.removePlotlines().removeMarkers();
  }

  public removePlotlines(): ChartOptionsBuilder {
    this.options.plotOptions = {
      ...this.options.plotOptions,
      series: {
        ...this.options.plotOptions?.series,
        lineWidth: 0,
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
      },
    };
    return this;
  }

  public removeMarkers(): ChartOptionsBuilder {
    this.options.plotOptions = {
      ...this.options.plotOptions,
      series: {
        ...this.options.plotOptions?.series,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
    };
    return this;
  }

  public setBackgroundColor(color: string): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      backgroundColor: color,
    };
    return this;
  }

  public setColors(colors: string[]): ChartOptionsBuilder {
    this.options.colors = colors;
    return this;
  }

  public setFontFamily(fontFamily: string): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      style: {
        ...this.options.chart.style,
        fontFamily: fontFamily,
      },
    };
    return this;
  }

  public setZoomType(type: OptionsChartZoomingTypeValue): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      zooming: {
        type: type,
      },
    };
    return this;
  }

  public distributeTicks(tickCount: number, axis: 'xAxis' | 'yAxis' | 'zAxis'): ChartOptionsBuilder {
    function positionTicksEven(): number[] {
      const positions: number[] = [];
      const increment: number = (this.dataMax - this.dataMin) / (tickCount - 1);

      if (this.dataMax !== null && this.dataMin !== null) {
        if (tickCount < 1) {
          return [this.dataMin, this.dataMax];
        }
        for (let i = 0; i < tickCount; i++) {
          positions.push(Math.round(this.dataMin + i * increment));
        }
      }
      return positions;
    }

    this.manipulateAxis(axis, axisOptions => ({
      ...axisOptions,
      tickPositioner: positionTicksEven,
    }));

    return this;
  }

  public setAxisType(type: Highcharts.AxisTypeValue, axis: 'xAxis' | 'yAxis' | 'zAxis'): ChartOptionsBuilder {
    this.manipulateAxis(axis, axisOptions => ({
      ...axisOptions,
      type: type,
    }));
    return this;
  }

  /**
   * Requires highcharts/highstock
   */
  public enableNavigator(enabled: boolean): ChartOptionsBuilder {
    this.options.navigator = {
      ...this.options.navigator,
      enabled: enabled,
    };
    return this;
  }

  public enableMousewheelZoom(enabled: boolean): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      zooming: {
        ...this.options.chart?.zooming,
        mouseWheel: enabled,
      },
    };
    return this;
  }

  public enableScrollbar(enabled: boolean): ChartOptionsBuilder {
    this.options.scrollbar = {
      ...this.options.scrollbar,
      enabled: enabled,
    };
    return this;
  }

  public setNavigatorXAxis(axisOptions: NavigatorXAxisOptions): ChartOptionsBuilder {
    this.options.navigator = {
      ...this.options.navigator,
      xAxis: {
        ...this.options.navigator?.xAxis,
        ...axisOptions,
      },
    };
    return this;
  }

  public enableCompare(): ChartOptionsBuilder {
    this.options.plotOptions = {
      ...this.options.plotOptions,
      series: {
        ...this.options.plotOptions?.series,
        compare: 'percent',
        compareStart: false
      },
    };
    return this;
  }

  public disableCompare(): ChartOptionsBuilder {
    if (this.options?.plotOptions?.series) {
      this.options.plotOptions.series = {
        ...this.options.plotOptions.series
      };
      delete this.options.plotOptions.series.compare;
      delete this.options.plotOptions.series.compareStart;
    }
    return this;
  }

  public mapSeries<T extends Highcharts.SeriesOptionsType>(mapFn: (series: T, index: number) => T): ChartOptionsBuilder {
    this.manipulateArray(this.options, 'series', mapFn);
    return this;
  }

  public setZoomCallback(callback: ChartOptionsZoomCallback): ChartOptionsBuilder {
    const fn: AxisSetExtremesEventCallbackFunction = !(callback instanceof Subject)
      ? callback
      : function (this, event) {
          callback.next([this, event]);
        };
    this.options.xAxis = {
      ...this.options.xAxis,
      events: {
        afterSetExtremes: fn,
      },
    };
    return this;
  }

  public setChartMargin(margin: number): ChartOptionsBuilder {
    this.options.chart = {
      ...this.options.chart,
      margin: margin,
    };
    return this;
  }

  public disableRangeSelector(): ChartOptionsBuilder {
    this.options.rangeSelector = {
      enabled: false,
    };
    return this;
  }

  private manipulateAxis<T extends 'xAxis' | 'yAxis' | 'zAxis'>(
    axis: T,
    callback: (axisOptions: HighchartsAxis<T>) => HighchartsAxis<T>
  ): void {
    const arg: HighchartsAxis<T> | Array<HighchartsAxis<T>> | undefined = this.options[axis] as any;
    if (Array.isArray(arg)) {
      for (let i = 0; i < arg.length; i++) {
        arg[i] = callback(arg[i]);
      }
    } else {
      this.options[axis] = callback(arg) as any;
    }
  }

  private manipulateArray<T, K extends keyof T, U>(data: T, key: K, callback: (value: U, index: number) => U) {
    const value: U | U[] = data[key] as U | U[];
    if (Array.isArray(value)) {
      const result: typeof value = [];
      for (let i = 0; i < value.length; i++) {
        result[i] = callback(value[i], i);
        data[key] = result as T[K];
      }
    } else {
      data[key] = callback(value, -1) as any;
    }
  }
}
